import { z } from 'zod';

/**
 * ISO 50001 EnMS workspace contract (CLAUDE.md §10 `/api/enms`, §11.1 `/iso50001`, §14).
 *
 * Single source of truth for the EnMS clause-workspace payloads — imported by the Worker
 * `enms` routes, their tests, and the React `/iso50001` screen. The workspace maps ISO 50001
 * clauses 4–10 to live system state; each item carries a status, an optional owner, an optional
 * due date (epoch ms), and an optional R2 evidence key (§7.2 `evidence/{site}/iso50001/...`).
 *
 * Mutations are RBAC-gated on `enms:write`; reads on `enms:read`. Every mutation writes an
 * immutable audit row (§12.3). IDs are generated server-side on create.
 */

// ── Status (mirrors db/schema.ts enms_items.status union) ────────────────────
export const EnmsStatusSchema = z.enum(['not_started', 'in_progress', 'complete', 'overdue']);
export type EnmsStatus = z.infer<typeof EnmsStatusSchema>;

/**
 * ISO 50001:2018 clause catalogue (clauses 4–10). Reference data shared by the API (to group
 * the status board and seed the workspace) and the web board. `clause` is the dotted top-level
 * number; `title` is the standard clause heading.
 */
export interface IsoClauseDef {
  readonly clause: string;
  readonly title: string;
}

export const ISO_50001_CLAUSES: readonly IsoClauseDef[] = [
  { clause: '4', title: 'Context of the organization' },
  { clause: '5', title: 'Leadership' },
  { clause: '6', title: 'Planning' },
  { clause: '7', title: 'Support' },
  { clause: '8', title: 'Operation' },
  { clause: '9', title: 'Performance evaluation' },
  { clause: '10', title: 'Improvement' },
] as const;

// ── Read row ──────────────────────────────────────────────────────────────────
export const EnmsItemRowSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  /** Dotted ISO clause reference, e.g. "6.3". */
  isoClause: z.string(),
  title: z.string(),
  status: EnmsStatusSchema,
  /** R2 object key for the attached evidence, or null when none uploaded. */
  evidenceR2Key: z.string().nullable(),
  /** Due date as epoch ms, or null. */
  due: z.number().nullable(),
  owner: z.string().nullable(),
});
export type EnmsItemRow = z.infer<typeof EnmsItemRowSchema>;

// ── List response ──────────────────────────────────────────────────────────────
export const EnmsListResponseSchema = z.object({ items: z.array(EnmsItemRowSchema) });
export type EnmsListResponse = z.infer<typeof EnmsListResponseSchema>;

// ── Mutation inputs ──────────────────────────────────────────────────────────
export const EnmsCreateSchema = z.object({
  siteId: z.string().min(1),
  isoClause: z.string().min(1),
  title: z.string().min(1),
  status: EnmsStatusSchema.optional(),
  due: z.number().nullable().optional(),
  owner: z.string().nullable().optional(),
});
export type EnmsCreate = z.infer<typeof EnmsCreateSchema>;

export const EnmsUpdateSchema = z
  .object({
    isoClause: z.string().min(1).optional(),
    title: z.string().min(1).optional(),
    status: EnmsStatusSchema.optional(),
    due: z.number().nullable().optional(),
    owner: z.string().nullable().optional(),
    /** Set/clear the evidence key directly (evidence upload also sets this server-side). */
    evidenceR2Key: z.string().nullable().optional(),
  })
  .refine((o) => Object.keys(o).length > 0, { message: 'at least one field required' });
export type EnmsUpdate = z.infer<typeof EnmsUpdateSchema>;

// ── Single-row response ──────────────────────────────────────────────────────
export const EnmsItemResponseSchema = z.object({ item: EnmsItemRowSchema });
export type EnmsItemResponse = z.infer<typeof EnmsItemResponseSchema>;

/**
 * Response from POST `/api/enms/:id/evidence` (multipart file upload → R2 → key stored on the
 * item). Returns the updated item plus the stored R2 key for convenience.
 */
export const EvidenceUploadResponseSchema = z.object({
  item: EnmsItemRowSchema,
  r2Key: z.string(),
});
export type EvidenceUploadResponse = z.infer<typeof EvidenceUploadResponseSchema>;
